import { useState } from "react";
import { ArrowLeft, ArrowRight, Plus, Trash2 } from "lucide-react";
import { useContent } from "@/content/ContentContext";
import { ImagePicker } from "@/admin/ImagePicker";
import { Modal, TextArea, Input, SaveBar, Label } from "@/admin/ui";

const setPath = (obj, path, value) => {
  const keys = path.split(".");
  const out = { ...obj };
  let cur = out;
  keys.forEach((k, i) => {
    if (i === keys.length - 1) cur[k] = value;
    else {
      cur[k] = { ...(cur[k] || {}) };
      cur = cur[k];
    }
  });
  return out;
};

// ---------- Testo IT / EN ----------
export const TextEditor = ({ path, values, onClose }) => {
  const { saveText } = useContent();
  const [it, setIt] = useState(values.it ?? "");
  const [en, setEn] = useState(values.en ?? "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      if (it !== values.it) await saveText("it", path, it);
      if (en !== values.en) await saveText("en", path, en);
      onClose();
    } finally {
      setSaving(false);
    }
  };
  const rows = Math.min(8, Math.max(2, Math.ceil((it || "").length / 60)));
  return (
    <Modal title="Modifica testo" onClose={onClose}>
      <div className="space-y-5">
        <TextArea label="Italiano" value={it} onChange={setIt} rows={rows} testId="text-editor-it" />
        <TextArea label="Inglese" value={en} onChange={setEn} rows={rows} testId="text-editor-en" />
        <p className="text-xs text-anthracite/50">Campo: {path}</p>
      </div>
      <SaveBar onSave={save} onCancel={onClose} saving={saving} />
    </Modal>
  );
};

// ---------- Immagine singola ----------
export const SingleImageEditor = ({ imgKey, onClose }) => {
  const { images, saveSection } = useContent();
  const current = imgKey.split(".").reduce((o, k) => o?.[k], images);
  const [selected, setSelected] = useState(current);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await saveSection("images", setPath(images, imgKey, selected));
      onClose();
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal title="Sostituisci foto" onClose={onClose} wide>
      <div className="grid sm:grid-cols-[180px_1fr] gap-6">
        <div>
          <Label>Anteprima</Label>
          <img src={selected} alt="" className="w-full aspect-[3/4] object-cover border border-sand" data-testid="single-image-preview" />
        </div>
        <ImagePicker onPick={setSelected} selected={selected} />
      </div>
      <SaveBar onSave={save} onCancel={onClose} saving={saving} />
    </Modal>
  );
};

// ---------- Lista di immagini (hero, sapori, gallery) ----------
const listTitles = { hero: "Slider Hero", sapori: "Slider Sapori", gallery: "Gallery" };

export const ListImageEditor = ({ listKey, startIndex = 0, onClose }) => {
  const { images, saveSection } = useContent();
  const [items, setItems] = useState(images[listKey]);
  const [active, setActive] = useState(startIndex);
  const [picking, setPicking] = useState(null); // "replace" | "add"
  const [saving, setSaving] = useState(false);

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
    setActive(j);
  };
  const remove = (i) => {
    setItems(items.filter((_, k) => k !== i));
    setActive(Math.max(0, i - 1));
  };
  const onPick = (src) => {
    if (picking === "add") {
      setItems([...items, src]);
      setActive(items.length);
    } else {
      setItems(items.map((s, k) => (k === active ? src : s)));
    }
    setPicking(null);
  };
  const save = async () => {
    setSaving(true);
    try {
      await saveSection("images", { ...images, [listKey]: items });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={listTitles[listKey] || listKey} onClose={onClose} wide>
      {picking ? (
        <>
          <button onClick={() => setPicking(null)} className="text-xs uppercase tracking-[0.2em] text-anthracite/60 hover:text-bordeaux mb-4">
            ← Torna alla lista
          </button>
          <ImagePicker onPick={onPick} selected={items[active]} />
        </>
      ) : (
        <>
          <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-3" data-testid="list-editor-grid">
            {items.map((src, i) => (
              <button
                key={`${src}-${i}`}
                onClick={() => setActive(i)}
                className={`relative aspect-[3/4] overflow-hidden border-2 ${active === i ? "border-bordeaux" : "border-transparent"}`}
                data-testid={`list-editor-item-${i}`}
              >
                <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
                <span className="absolute top-1 left-1 bg-anthracite/70 text-ivory text-[10px] px-1.5 py-0.5">{i + 1}</span>
              </button>
            ))}
            <button
              onClick={() => setPicking("add")}
              className="aspect-[3/4] border-2 border-dashed border-sand flex flex-col items-center justify-center gap-1 text-xs text-anthracite/60 hover:border-bordeaux hover:text-bordeaux"
              data-testid="list-editor-add"
            >
              <Plus className="w-5 h-5" />
              Aggiungi
            </button>
          </div>
          {items.length > 0 && (
            <div className="mt-5 flex flex-wrap items-center gap-2 text-xs">
              <span className="text-anthracite/60 mr-2">Foto {active + 1} di {items.length}:</span>
              <button onClick={() => move(active, -1)} className="btn-outline !px-3 !py-2 !text-[11px]" data-testid="list-editor-move-left"><ArrowLeft className="w-3.5 h-3.5" /> Sposta</button>
              <button onClick={() => move(active, 1)} className="btn-outline !px-3 !py-2 !text-[11px]" data-testid="list-editor-move-right">Sposta <ArrowRight className="w-3.5 h-3.5" /></button>
              <button onClick={() => setPicking("replace")} className="btn-outline !px-3 !py-2 !text-[11px]" data-testid="list-editor-replace">Sostituisci</button>
              <button onClick={() => remove(active)} className="btn-outline !px-3 !py-2 !text-[11px] !text-bordeaux" data-testid="list-editor-remove"><Trash2 className="w-3.5 h-3.5" /> Rimuovi</button>
            </div>
          )}
          <SaveBar onSave={save} onCancel={onClose} saving={saving} />
        </>
      )}
    </Modal>
  );
};

// ---------- Formula (foto + menù a tendina) ----------
const emptyItem = () => ({ name: { it: "", en: "" }, description: { it: "", en: "" }, price: "" });

export const FormulaEditor = ({ formulaId, onClose }) => {
  const { formule, saveSection } = useContent();
  const idx = formule.findIndex((f) => f.id === formulaId);
  const [formula, setFormula] = useState(formule[idx]);
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState(false);
  const menu = formula.menu || [];
  const setItem = (i, patch) => setFormula({ ...formula, menu: menu.map((m, k) => (k === i ? { ...m, ...patch } : m)) });
  const save = async () => {
    setSaving(true);
    try {
      await saveSection("formule", formule.map((f, k) => (k === idx ? formula : f)));
      onClose();
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal title={`Formula: ${formulaId}`} onClose={onClose} wide>
      {picking ? (
        <>
          <button onClick={() => setPicking(false)} className="text-xs uppercase tracking-[0.2em] text-anthracite/60 hover:text-bordeaux mb-4">← Indietro</button>
          <ImagePicker selected={formula.image} onPick={(src) => { setFormula({ ...formula, image: src }); setPicking(false); }} />
        </>
      ) : (
        <>
          <div className="flex items-center gap-4">
            <img src={formula.image} alt="" className="w-28 aspect-[16/10] object-cover border border-sand" />
            <button onClick={() => setPicking(true)} className="btn-outline !px-4 !py-2 !text-[11px]" data-testid="formula-change-image">Cambia foto</button>
          </div>
          <p className="mt-8 font-serif text-lg">Voci del menù a tendina</p>
          <p className="text-xs text-anthracite/55 mt-1">Il titolo e la descrizione della formula si modificano cliccandoli direttamente in pagina.</p>
          <div className="mt-4 space-y-5" data-testid="formula-menu-items">
            {menu.map((item, i) => (
              <div key={i} className="border border-sand p-4 grid sm:grid-cols-2 gap-3">
                <Input label="Nome (IT)" value={item.name.it} onChange={(v) => setItem(i, { name: { ...item.name, it: v } })} testId={`formula-item-name-it-${i}`} />
                <Input label="Nome (EN)" value={item.name.en} onChange={(v) => setItem(i, { name: { ...item.name, en: v } })} />
                <Input label="Descrizione (IT)" value={item.description?.it || ""} onChange={(v) => setItem(i, { description: { ...item.description, it: v } })} />
                <Input label="Descrizione (EN)" value={item.description?.en || ""} onChange={(v) => setItem(i, { description: { ...item.description, en: v } })} />
                <Input label="Prezzo (€)" value={item.price || ""} onChange={(v) => setItem(i, { price: v })} testId={`formula-item-price-${i}`} />
                <div className="flex items-end justify-end">
                  <button onClick={() => setFormula({ ...formula, menu: menu.filter((_, k) => k !== i) })} className="btn-outline !px-3 !py-2 !text-[11px] !text-bordeaux"><Trash2 className="w-3.5 h-3.5" /> Rimuovi</button>
                </div>
              </div>
            ))}
            <button onClick={() => setFormula({ ...formula, menu: [...menu, emptyItem()] })} className="btn-outline !px-4 !py-2 !text-[11px]" data-testid="formula-add-item"><Plus className="w-3.5 h-3.5" /> Aggiungi voce</button>
          </div>
          <SaveBar onSave={save} onCancel={onClose} saving={saving} />
        </>
      )}
    </Modal>
  );
};

// ---------- Recensioni ----------
const emptyReview = () => ({ author: "", date: { it: "", en: "" }, it: "", en: "" });

export const ReviewsEditor = ({ startIndex = 0, onClose }) => {
  const { reviews, reviewsMeta, saveSection } = useContent();
  const [list, setList] = useState(reviews);
  const [meta, setMeta] = useState(reviewsMeta);
  const [active, setActive] = useState(startIndex);
  const [saving, setSaving] = useState(false);
  const r = list[active];
  const set = (patch) => setList(list.map((x, k) => (k === active ? { ...x, ...patch } : x)));
  const save = async () => {
    setSaving(true);
    try {
      await saveSection("reviews", list);
      await saveSection("reviewsMeta", meta).catch(() => {});
      onClose();
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal title="Recensioni" onClose={onClose} wide>
      <div className="grid sm:grid-cols-3 gap-3">
        <Input label="Punteggio" value={meta.rating} onChange={(v) => setMeta({ ...meta, rating: v })} />
        <Input label="Numero recensioni" value={String(meta.count)} onChange={(v) => setMeta({ ...meta, count: v })} />
        <Input label="Fonte" value={meta.source} onChange={(v) => setMeta({ ...meta, source: v })} />
        <div className="sm:col-span-3"><Input label="Link alla pagina recensioni" value={meta.url} onChange={(v) => setMeta({ ...meta, url: v })} /></div>
      </div>
      <div className="mt-6 flex flex-wrap gap-2" data-testid="reviews-editor-tabs">
        {list.map((x, i) => (
          <button key={i} onClick={() => setActive(i)} className={`px-3 py-1.5 text-xs border ${active === i ? "border-bordeaux text-bordeaux" : "border-sand"}`} data-testid={`reviews-editor-tab-${i}`}>
            {i + 1}. {x.author || "Nuova"}
          </button>
        ))}
        <button onClick={() => { setList([...list, emptyReview()]); setActive(list.length); }} className="px-3 py-1.5 text-xs border border-dashed border-sand hover:border-bordeaux" data-testid="reviews-editor-add"><Plus className="w-3 h-3 inline" /> Aggiungi</button>
      </div>
      {r && (
        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          <Input label="Autore" value={r.author} onChange={(v) => set({ author: v })} testId="review-author" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Data (IT)" value={r.date?.it || ""} onChange={(v) => set({ date: { ...r.date, it: v } })} />
            <Input label="Data (EN)" value={r.date?.en || ""} onChange={(v) => set({ date: { ...r.date, en: v } })} />
          </div>
          <TextArea label="Testo (IT)" value={r.it} onChange={(v) => set({ it: v })} rows={4} testId="review-text-it" />
          <TextArea label="Testo (EN)" value={r.en} onChange={(v) => set({ en: v })} rows={4} />
          <div className="sm:col-span-2 flex justify-end">
            <button onClick={() => { setList(list.filter((_, k) => k !== active)); setActive(0); }} className="btn-outline !px-3 !py-2 !text-[11px] !text-bordeaux" data-testid="review-remove"><Trash2 className="w-3.5 h-3.5" /> Elimina recensione</button>
          </div>
        </div>
      )}
      <SaveBar onSave={save} onCancel={onClose} saving={saving} />
    </Modal>
  );
};

// ---------- Info locale ----------
const siteFields = [
  ["phoneDisplay", "Telefono (come mostrato)"],
  ["phoneHref", "Link telefono (es. tel:+39...)"],
  ["whatsapp", "Numero WhatsApp (solo cifre, con prefisso 39)"],
  ["address", "Indirizzo"],
  ["mapsUrl", "Link Google Maps"],
  ["hours", "Orari di apertura (lascia vuoto per 'in aggiornamento')"],
  ["instagram", "Link Instagram"],
  ["menuPdf", "Link PDF menù (opzionale)"],
];

export const SiteEditor = ({ onClose }) => {
  const { site, saveSection } = useContent();
  const [draft, setDraft] = useState(site);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await saveSection("site", draft);
      onClose();
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal title="Info locale" onClose={onClose}>
      <div className="space-y-4">
        {siteFields.map(([key, label]) => (
          <Input key={key} label={label} value={draft[key] || ""} onChange={(v) => setDraft({ ...draft, [key]: v })} testId={`site-field-${key}`} />
        ))}
      </div>
      <SaveBar onSave={save} onCancel={onClose} saving={saving} />
    </Modal>
  );
};
